import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as actions from '../../actions';
import { Col, Row, Button, UncontrolledTooltip } from 'reactstrap';
import {
  PricingItem,
  PricingItemTitleSB as PricingItemTitle,
  PricingItemListItem,
  PricingItemList,
  PricingSwitch
} from '../../elements/Pricing';
import { RedeemDiscountKey } from '../../elements/DiscountKey';
import queryString from 'query-string';
import { getTranslationKey } from '../../modules/utils/helpers';
import type { Notify } from '../../typings/redux';

interface PricingPlan {
  order: number;
  type: string;
  yearly: number;
  monthly: number;
  intervall: string;
  months: number;
}

const pricingPlans: PricingPlan[] = [
  {
    order: 1,
    type: 'ultimate',
    yearly: 24,
    monthly: 29,
    intervall: 'yearly',
    months: 12
  },
  {
    order: 2,
    type: 'ultimate',
    yearly: 19,
    monthly: 24,
    intervall: 'twoYearly',
    months: 24
  }
];

interface DiscountKey {
  first_percent?: number;
  percent?: number;
  _id?: string;
  affiliate?: string;
  noMinimumContractLength?: boolean;
}

interface Digistore {
  discountKey?: DiscountKey;
}

interface Auth {
  digistore?: Digistore;
  authenticated?: boolean;
}

interface ScrAppBookPricingProps {
  auth: Auth;
  notify: Notify;
  authentication?: Auth;
}

interface ScrAppBookPricingState {
  intervall: 'monthly' | 'yearly';
}

class ScrAppBookPricing extends Component<ScrAppBookPricingProps, ScrAppBookPricingState> {
  constructor(props: ScrAppBookPricingProps) {
    super(props);

    this.state = {
      intervall: 'yearly'
    };
    this.onClickPricingSwitch = this.onClickPricingSwitch.bind(this);
  }

  onClickPricingSwitch(event: React.MouseEvent<HTMLElement>): void {
    const target = event.target as HTMLElement;
    if (!target.className) {
      this.setState((prevState) => ({
        intervall: prevState.intervall === 'monthly' ? 'yearly' : 'monthly'
      }));
    }
  }

  renderPriceItemList(plan: string, intervall: string, yearlyPlan = false): JSX.Element {
    return (
      <PricingItemList>
        <PricingItemListItem noBulletPoint>
          <h3>
            <strong>
              {getTranslationKey(`pricingTables.sb_storage_${plan}`)}{' '}
              {getTranslationKey('pricingTables.storage')}
            </strong>
          </h3>
        </PricingItemListItem>

        {yearlyPlan && (
          <PricingItemListItem>
            {intervall === 'twoYearly'
              ? getTranslationKey('pricingTables.sb_features.0_twoYearly')
              : getTranslationKey('pricingTables.sb_features.0')}

            <i id={intervall + '_0'} className="m-l-5 fa fa-icon fa-question-circle" />
            <UncontrolledTooltip placement="right" target={intervall + '_0'}>
              {intervall === 'twoYearly'
                ? getTranslationKey('pricingTables.sb_features.0_twoYearly_tt')
                : getTranslationKey('pricingTables.sb_features.0_tt')}
            </UncontrolledTooltip>
          </PricingItemListItem>
        )}

        <PricingItemListItem>
          {getTranslationKey('pricingTables.sb_features.1')}
          <i id={intervall + '_1'} className="m-l-5 fa fa-icon fa-question-circle" />
          <UncontrolledTooltip placement="right" target={intervall + '_1'}>
            {getTranslationKey('pricingTables.sb_features.1_tt')}
          </UncontrolledTooltip>
        </PricingItemListItem>
        <PricingItemListItem>
          {getTranslationKey('pricingTables.sb_features.2')}
          <i id={intervall + '_2'} className="m-l-5 fa fa-icon fa-question-circle" />
          <UncontrolledTooltip placement="right" target={intervall + '_2'}>
            {getTranslationKey('pricingTables.sb_features.2_tt')}
          </UncontrolledTooltip>
        </PricingItemListItem>

        <PricingItemListItem>
          {getTranslationKey('pricingTables.sb_features.3')}
          <i id={intervall + '_3'} className="m-l-5 fa fa-icon fa-question-circle" />
          <UncontrolledTooltip placement="right" target={intervall + '_3'}>
            {getTranslationKey('pricingTables.sb_features.3_tt')}
          </UncontrolledTooltip>
        </PricingItemListItem>

        <PricingItemListItem>
          {intervall === 'twoYearly'
            ? getTranslationKey('pricingTables.sb_features.4_twoYearly')
            : getTranslationKey('pricingTables.sb_features.4')}

          <i id={intervall + '_4'} className="m-l-5 fa fa-icon fa-question-circle" />
          <UncontrolledTooltip placement="right" target={intervall + '_4'}>
            {intervall === 'twoYearly'
              ? getTranslationKey('pricingTables.sb_features.4_twoYearly_tt')
              : getTranslationKey('pricingTables.sb_features.4_tt')}
          </UncontrolledTooltip>
        </PricingItemListItem>

        <PricingItemListItem>
          {getTranslationKey('pricingTables.sb_features.5')}
          <i id={intervall + '_5'} className="m-l-5 fa fa-icon fa-question-circle" />
          <UncontrolledTooltip placement="right" target={intervall + '_5'}>
            {getTranslationKey('pricingTables.sb_features.5_tt')}
          </UncontrolledTooltip>
        </PricingItemListItem>

        <PricingItemListItem>
          {intervall === 'twoYearly'
            ? getTranslationKey('pricingTables.sb_features.6_twoYearly')
            : getTranslationKey('pricingTables.sb_features.6')}

          <i id={intervall + '_6'} className="m-l-5 fa fa-icon fa-question-circle" />
          <UncontrolledTooltip placement="right" target={intervall + '_6'}>
            {getTranslationKey('pricingTables.sb_features.6_tt')}
          </UncontrolledTooltip>
        </PricingItemListItem>
      </PricingItemList>
    );
  }

  renderPricingButton(intervall: string, plan: string): JSX.Element {
    const {
      auth: { digistore, authenticated }
    } = this.props;

    const query = queryString.stringify({
      discountKey: digistore && digistore.discountKey && digistore.discountKey._id,
      affiliate: digistore && digistore.discountKey && digistore.discountKey.affiliate
    });

    if (!authenticated) {
      return (
        <Button
          color="success"
          href={
            `${window.API_URL}/api/guest/subscription/buy/scrappbook/${plan}/${intervall}` +
            (query ? '?' + query : '')
          }
        >
          {getTranslationKey('pricingTables.startNow')}
        </Button>
      );
    }

    return (
      <Button
        size="xl"
        color="success"
        href={
          `${window.API_URL}/api/subscription/buy/scrappbook/${plan}/${intervall}` +
          (query ? '?' + query : '')
        }
      >
        {getTranslationKey('pricingTables.startNow')}
      </Button>
    );
  }

  renderPricingPlans(): JSX.Element {
    const { intervall } = this.state;
    const {
      auth: { digistore }
    } = this.props;
    const noMinimumContractLength =
      digistore && digistore.discountKey && digistore.discountKey.noMinimumContractLength;

    return intervall === 'monthly' ? (
      <Row className="justify-content-center">
        {pricingPlans.map((plan) =>
          plan.monthly && (!noMinimumContractLength || plan.months === 12) ? (
            <PricingItem xl="4" lg="5" md="12" key={plan.order}>
              <PricingItemTitle
                plan={plan.type}
                price={plan.monthly}
                currency="€"
                discount={digistore && digistore.discountKey}
                noMinimumContractLength={noMinimumContractLength}
                intervall={
                  `monthly${plan.intervall.charAt(0).toUpperCase()}${plan.intervall.slice(1)}` as
                    | 'monthlyYearly'
                    | 'monthlyTwoYearly'
                }
              />
              {this.renderPriceItemList(plan.type, plan.intervall)}
              {this.renderPricingButton(
                `monthly${plan.intervall.charAt(0).toUpperCase()}${plan.intervall.slice(1)}`,
                plan.type
              )}
            </PricingItem>
          ) : null
        )}
      </Row>
    ) : (
      <Row className="justify-content-center">
        {pricingPlans.map((plan) => (
          <PricingItem
            xl="4"
            lg="5"
            md="12"
            key={plan.order}
            className={plan.intervall === 'yearly' ? 'best-choice' : ''}
            mostSelected={plan.intervall === 'yearly'}
            bestPrice={plan.intervall === 'twoYearly'}
          >
            <PricingItemTitle
              plan={plan.type}
              price={plan.yearly}
              currency="€"
              discount={digistore && digistore.discountKey}
              intervall={plan.intervall}
            />
            {this.renderPriceItemList(plan.type, plan.intervall, true)}
            {this.renderPricingButton(plan.intervall, plan.type)}
          </PricingItem>
        ))}
      </Row>
    );
  }

  render(): JSX.Element | null {
    const { intervall } = this.state;
    const { auth } = this.props;
    const { digistore } = auth;

    console.log(digistore && digistore.discountKey);

    return digistore ? (
      <div className="sb-pricing animated fadeIn">
        <div className="pricing-view">
          <Row className="justify-content-center">
            <Col xs="8" className="text-center">
              <h1 className="m-b-20">{getTranslationKey('pricingTables.sb_title')}</h1>
              <h4 className="m-b-15">{getTranslationKey('pricingTables.sb_subtitle')}</h4>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col sm="12" md="6" className="text-center">
              <PricingSwitch intervall={intervall} onClick={this.onClickPricingSwitch} />
            </Col>
          </Row>
          <Row className="justify-content-center text-center">
            <div className="p-r-15 p-l-15">
              <p className="text-muted">{getTranslationKey('pricingTables.netPrices')}</p>
            </div>
          </Row>
          {this.renderPricingPlans()}

          {auth.authenticated && (
            <Row className="justify-content-center m-t-40 text-center">
              <div className="p-r-15 p-l-15">
                <RedeemDiscountKey />
                {getTranslationKey('pricingTables.howToVideo') && (
                  <p>
                    {getTranslationKey('pricingTables.howToVideo')}
                    {': '}
                    <a
                      href="https://help.scrappbook.de/article/automatische-loeschung-der-hochaufloesenden-bilder/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {getTranslationKey('pricingTables.howToVideoLink')}
                    </a>
                  </p>
                )}
              </div>
            </Row>
          )}
        </div>
      </div>
    ) : null;
  }
}

interface RootState {
  auth: Auth;
  notify: Notify;
}

interface OwnProps {
  authentication?: Auth;
}

function mapStateToProps({ auth, notify }: RootState, { authentication }: OwnProps) {
  return { auth: authentication || auth, notify };
}

export default connect(mapStateToProps, actions)(ScrAppBookPricing);
